import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  removeAgentFromAgency,
  respondToPartnerInvite,
  resendPartnerInvite,
  removeShowcasePartner,
  staffMandateService,
  authService,
} from "../../services/api";
// B4 "Submit license" — reuse of the EXISTING license-submission modal
// (My Profile → Update License; updateAgentLicense endpoint, verification
// backend-side). No new review flow.
import { LicenseUpdateModal } from "../../components/profile/AgentAccountInfo";
import { STAFF_MANDATE_ENABLED } from "../../config/featureFlags";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { ROLES } from "../../config/roles";
import useAgencyProfile from "../../hooks/agency/useAgencyProfile";
// Wave-2 shell data (F-1/F-2/F-3): additive hook OUTSIDE the shared
// useAgencyProfile (untouchable) — tab counts + subtitle + trust stats.
import useAgencyShellData from "../../hooks/agency/useAgencyShellData";
import AgentInvitationModal from "../../components/agency/AgentInvitationModal";
import ChangeAgentModal from "../../components/agency/ChangeAgentModal";
import PartnerInvitationModal from "../../components/agency/PartnerInvitationModal";
import PartnerConfirmDialog from "../../components/agency/PartnerConfirmDialog";
import IncomingPartnerInvitesPanel from "../../components/agency/IncomingPartnerInvitesPanel";
import AgencyProfileMobileHeader from "../../components/agency/responsive/AgencyProfileMobileHeader";
import AgencyProfileMobileTabs from "../../components/agency/responsive/AgencyProfileMobileTabs";
import PositionFilterDropdown from "../../components/common/PositionFilterDropdown";
import AgencyTabs from "../../components/agency/AgencyTabs";
import AgencySubtitle from "../../components/agency/AgencySubtitle";
import TrustStatsRow from "../../components/agency/TrustStatsRow";
import AgentsToolbar from "../../components/agency/AgentsToolbar";
import PlayersToolbar from "../../components/agency/PlayersToolbar";
import PendingInvitationsPanel from "../../components/agency/PendingInvitationsPanel";
import AgentsGrid from "../../components/agency/AgentsGrid";
import PlayersGrid from "../../components/agency/PlayersGrid";
import PartnersGrid from "../../components/agency/PartnersGrid";
// TP-Coach-Mandate: staff variants (clones — PlayersGrid/ChangeAgentModal
// stay byte-identical).
import StaffGrid from "../../components/agency/StaffGrid";
import ChangeStaffAgentModal from "../../components/agency/ChangeStaffAgentModal";
import { ConfirmDialog, ShieldIcon, ShieldXIcon } from "../../components/agency/v2";

// System-derived: an agent is licensed when the backend `is_licensed` flag is
// true (a89158b); fall back to license_number for older cached rows.
const isAgentLicensed = (a) =>
  a?.is_licensed === true || (a?.is_licensed === undefined && Boolean(a?.license_number));

const AgencyProfile = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  // Only agency admins may promote/demote/remove agents. Regular agents can now
  // reach this page (route relaxed for multi-admin), so gate the admin-action
  // menu by role — the backend already enforces this (403), this just hides
  // buttons that would error for non-admins.
  const { currentRole, user } = useSelector((state) => state.auth);
  const isCurrentUserAdmin = currentRole === ROLES.AgencyAdmin;
  // Used to hide the admin-action menu on the current user's own card —
  // you can't promote/demote/remove yourself from here.
  const currentUserAccountId = user?.account_id || user?.id || null;

  const {
    // Tab states
    activeTab,
    setActiveTab,
    activeAgentType,
    selectedPosition,
    setSelectedPosition,
    playerSort,
    setPlayerSort,
    contractFilter,
    setContractFilter,
    expiringSoon,
    setExpiringSoon,
    managingAgentFilter,
    setManagingAgentFilter,
    nationalityFilter,
    setNationalityFilter,
    agentOptions,
    nationalityOptions,
    playerSearch,
    setPlayerSearch,
    playerCounts,

    // Data
    agents,
    agentsLoading,
    agentsError,
    playersData,
    playersLoading,
    playersError,
    partnersData,
    partnersLoading,
    partnersError,

    // Filtered data
    positions,

    // Modal states
    showAgentInviteModal,
    setShowAgentInviteModal,
    showPartnerInviteModal,
    setShowPartnerInviteModal,
    showChangeAgentModal,
    setShowChangeAgentModal,

    // Popover states
    popoverAgent,
    showPopover,
    setShowPopover,
    popoverPlayer,
    showPlayerPopover,
    setShowPlayerPopover,

    // Other states
    transferringAgentId,
    selectedPlayer,

    // Fetch functions
    fetchAgents,
    fetchPartners,

    // Handlers
    handleAgentMenuClick,
    handleMakeAdmin,
    handleRemoveAdmin,
    adminActionTarget,
    adminActionLoading,
    confirmAdminAction,
    cancelAdminAction,
    handlePlayerMenuClick,
    handleChangeAgent,
    handleChangeAgentSuccess,
    handlePlayerClick,
    handleAgentClick,
    handlePartnerClick,
  } = useAgencyProfile();

  // ── TP-Coach-Mandate: Staff tab (flag-gated, Brief §4.6) ──────────────
  // Local, additive state — the shared useAgencyProfile hook is untouched.
  const [staffData, setStaffData] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);
  const [popoverStaffMember, setPopoverStaffMember] = useState(null);
  const [showStaffPopover, setShowStaffPopover] = useState(false);
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const [showChangeStaffAgentModal, setShowChangeStaffAgentModal] = useState(false);

  const fetchStaffBoard = useCallback(async () => {
    setStaffLoading(true);
    setStaffError(null);
    const res = await staffMandateService.getAgencyStaffBoard();
    if (res.success) {
      setStaffData(res.data);
    } else {
      setStaffError(res.error || "Failed to fetch staff");
    }
    setStaffLoading(false);
  }, []);

  useEffect(() => {
    if (STAFF_MANDATE_ENABLED && activeTab === "staff") {
      fetchStaffBoard();
    }
  }, [activeTab, fetchStaffBoard]);

  const handleStaffMenuClick = (member) => {
    setPopoverStaffMember(member);
    setShowStaffPopover(true);
  };

  const handleChangeStaffAgent = (member) => {
    setSelectedStaffMember(member);
    setShowChangeStaffAgentModal(true);
  };

  const handleChangeStaffAgentSuccess = () => {
    setShowChangeStaffAgentModal(false);
    setSelectedStaffMember(null);
    fetchStaffBoard();
  };

  const renderStaffTab = () => (
    <StaffGrid
      members={staffData}
      staffLoading={staffLoading}
      staffError={staffError}
      showStaffPopover={showStaffPopover}
      popoverMember={popoverStaffMember}
      onStaffMenuClick={handleStaffMenuClick}
      onCloseStaffPopover={() => setShowStaffPopover(false)}
      onChangeAgent={handleChangeStaffAgent}
      // V2 empty-state CTA — SAME navigate dispatch as the tab-chrome
      // "Invite staff" button (presentational addition only).
      onInviteStaff={() =>
        navigate("/agency/invite-staff", { state: { mode: "agency" } })
      }
      isCurrentUserAdmin={isCurrentUserAdmin}
    />
  );
  // ── end TP-Coach-Mandate block ────────────────────────────────────────

  // Agents toolbar state (segment + search) — filtering is client-side over
  // the full roster so counts stay live (design 1a/1c).
  const [agentSegment, setAgentSegment] = useState("all");
  const [agentSearch, setAgentSearch] = useState("");
  const [pendingReloadKey, setPendingReloadKey] = useState(0);

  const agentCounts = useMemo(() => {
    const licensed = agents.filter(isAgentLicensed).length;
    return { all: agents.length, licensed, unlicensed: agents.length - licensed };
  }, [agents]);

  // F-8 — last-admin guard (board 1k demo 3): admin count derived
  // client-side from the already-loaded roster; the backend 400 guard
  // stays authoritative.
  const adminCount = useMemo(
    () =>
      agents.filter(
        (a) => a.is_admin === true || a?.position === "Agency Admin"
      ).length,
    [agents]
  );

  // B4 — own roster row (for the quiet "Submit license" affordance on the
  // own card while unlicensed; unlicensed is a legitimate state — no
  // banner, no reminder, no badge pressure).
  const ownAgentRow = useMemo(
    () =>
      agents.find(
        (a) =>
          currentUserAccountId != null &&
          String(a.account_id) === String(currentUserAccountId)
      ) || null,
    [agents, currentUserAccountId]
  );
  const [showSubmitLicense, setShowSubmitLicense] = useState(false);
  const [submittingLicense, setSubmittingLicense] = useState(false);
  const handleSubmitLicense = async (licenseData) => {
    setSubmittingLicense(true);
    try {
      // EXISTING submission mechanic (POST /users/update-agent-license/);
      // "licensed" flips only after backend verification — same as the
      // My Profile flow this reuses.
      const res = await authService.updateAgentLicense(licenseData);
      if (res.success) {
        toast.success("License submitted.");
        setShowSubmitLicense(false);
        fetchAgents(activeAgentType);
      } else {
        toast.error(
          typeof res.error === "string" ? res.error : "Failed to submit license"
        );
      }
    } finally {
      setSubmittingLicense(false);
    }
  };

  // Wave-2 shell data (F-1/F-2/F-3, BE PR #170): one tab-counts fetch
  // serves the count pills AND the page subtitle; trust stats are their
  // own endpoint. Fail-silent — see useAgencyShellData.
  const { shellCounts, trustStats, refreshShellCounts } = useAgencyShellData();

  // F-3 — V2 tab-chrome count pills (ARTBOARD-SPEC CMP-TABS): live
  // counts on every tab from GET /users/agency/tab-counts. BE contract:
  // `staff` is null when the caller can't see the staff board (flag off
  // or non-admin) → NO staff pill then (not 0); `players` is role-scoped
  // like the players board. If the endpoint errored (shellCounts null),
  // fall back to the previous derived behavior — a pill appears once
  // that tab's data has loaded (never block the page on counts).
  const tabCounts = useMemo(() => {
    if (shellCounts) {
      return {
        agents: shellCounts.agents ?? null,
        players: shellCounts.players ?? null,
        staff: shellCounts.staff ?? null,
        partners: shellCounts.partners ?? null,
      };
    }
    return {
      agents: agents.length > 0 ? agents.length : null,
      players: playerCounts?.total ?? null,
      staff: staffData.length > 0 ? staffData.length : null,
      partners: partnersData.length > 0 ? partnersData.length : null,
    };
  }, [shellCounts, agents, playerCounts, staffData, partnersData]);

  const visibleAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    return agents.filter((a) => {
      if (agentSegment === "licensed" && !isAgentLicensed(a)) return false;
      if (agentSegment === "unlicensed" && isAgentLicensed(a)) return false;
      if (!q) return true;
      return (
        (a.name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q)
      );
    });
  }, [agents, agentSegment, agentSearch]);

  const handleRemoveFromAgency = async (agent) => {
    if (!agent) return;
    const res = await removeAgentFromAgency(agent.account_id);
    if (res.success) {
      toast.success(`${agent.name || "Agent"} removed from the agency.`);
      fetchAgents(activeAgentType);
      // F-3: roster changed — refresh the tab-count pills alongside the
      // existing roster refetch (trivially wireable refresh point).
      refreshShellCounts();
    } else {
      const msg = typeof res.error === "string" ? res.error : "Failed to remove agent";
      toast.error(msg);
    }
  };

  // Any active narrowing on the players board (used to pick the right empty
  // state: 2c "No players yet" for a truly empty roster vs. the existing
  // "No players found" copy for a zero-result filter set).
  const hasActivePlayerFilters = Boolean(
    (playerSearch && playerSearch.trim()) ||
      contractFilter ||
      expiringSoon ||
      managingAgentFilter ||
      nationalityFilter ||
      (selectedPosition && selectedPosition !== "all")
  );

  const renderPlayersTab = () => (
    <>
      <PlayersToolbar
        counts={playerCounts}
        contractFilter={contractFilter}
        onContractFilter={setContractFilter}
        expiringSoon={expiringSoon}
        onExpiringSoon={setExpiringSoon}
        sort={playerSort}
        onSort={setPlayerSort}
        search={playerSearch}
        onSearch={setPlayerSearch}
        managingAgentFilter={managingAgentFilter}
        onManagingAgent={setManagingAgentFilter}
        agentOptions={agentOptions}
        nationalityFilter={nationalityFilter}
        onNationality={setNationalityFilter}
        nationalityOptions={nationalityOptions}
      />

      {/* Position filter */}
      <div className="px-6 pt-3">
        <PositionFilterDropdown
          positions={positions}
          selectedId={selectedPosition}
          onSelect={setSelectedPosition}
        />
      </div>

      <PlayersGrid
        players={playersData}
        playersLoading={playersLoading}
        playersError={playersError}
        showPlayerPopover={showPlayerPopover}
        popoverPlayer={popoverPlayer}
        onPlayerMenuClick={handlePlayerMenuClick}
        onClosePlayerPopover={() => setShowPlayerPopover(false)}
        onChangeAgent={handleChangeAgent}
        onPlayerClick={handlePlayerClick}
        // 2c empty-state CTA — SAME dispatch as the tab-chrome "Add player"
        // button (existing navigation, new artboard placement only).
        onAddPlayer={() =>
          navigate("/agency/invite-player", { state: { mode: "agency" } })
        }
        hasActiveFilters={hasActivePlayerFilters}
        // Board 2b: the expiring-soon view renders the compact card variant.
        compact={expiringSoon}
        isCurrentUserAdmin={isCurrentUserAdmin}
        // F-7: any filter/search/sort change collapses the grid again.
        resetKey={[
          playerSearch,
          contractFilter,
          expiringSoon,
          managingAgentFilter,
          nationalityFilter,
          selectedPosition,
          playerSort,
        ].join("|")}
      />
    </>
  );

  // Partner destructive actions (revoke pending / unlink linked / remove
  // showcase) go through a confirm dialog, then refresh wall + incoming panel.
  const [partnerAction, setPartnerAction] = useState(null); // {partner, kind}
  const [partnerActionLoading, setPartnerActionLoading] = useState(false);
  const [partnerReloadKey, setPartnerReloadKey] = useState(0);

  const refreshPartners = () => {
    fetchPartners();
    setPartnerReloadKey((k) => k + 1);
    // F-3: partner wall changed — keep the Partners tab pill in sync.
    refreshShellCounts();
  };

  const confirmPartnerAction = async () => {
    if (!partnerAction?.partner) return;
    const { partner, kind } = partnerAction;
    setPartnerActionLoading(true);
    try {
      const res =
        kind === "remove"
          ? await removeShowcasePartner(partner.id)
          : await respondToPartnerInvite(partner.id, "revoke");
      if (res.success) {
        toast.success(kind === "remove" ? "Partner removed." : "Partnership removed.");
        setPartnerAction(null);
        refreshPartners();
      } else {
        toast.error(typeof res.error === "string" ? res.error : "Action failed");
      }
    } finally {
      setPartnerActionLoading(false);
    }
  };

  // P-1 — resend a pending partnership invite (board 4c menu, SYM-26).
  // True resend: status stays pending, so no wall refresh is needed —
  // success/4xx surface as toasts (existing partner-toast pattern), with
  // a per-invite busy state while the request is in flight.
  const [resendingPartnerId, setResendingPartnerId] = useState(null);
  const handleResendPartnerInvite = async (partner) => {
    if (!partner || resendingPartnerId != null) return;
    setResendingPartnerId(partner.id);
    try {
      const res = await resendPartnerInvite(partner.id);
      if (res.success) {
        toast.success("Invite resent.");
      } else {
        // 4xx bodies are {detail} (400 non-pending / 403 / 404).
        const msg =
          typeof res.error === "string"
            ? res.error
            : res.error?.detail || "Failed to resend invite";
        toast.error(msg);
      }
    } finally {
      setResendingPartnerId(null);
    }
  };

  const renderPartnersTab = () => (
    <>
      {isCurrentUserAdmin && (
        <IncomingPartnerInvitesPanel
          reloadKey={partnerReloadKey}
          onChanged={refreshPartners}
          onViewProfile={handlePartnerClick}
        />
      )}
      <PartnersGrid
        partners={partnersData}
        partnersLoading={partnersLoading}
        partnersError={partnersError}
        onPartnerClick={handlePartnerClick}
        onAddPartner={() => setShowPartnerInviteModal(true)}
        onPendingMenu={(p) => setPartnerAction({ partner: p, kind: "revoke" })}
        onResendInvite={handleResendPartnerInvite}
        resendingPartnerId={resendingPartnerId}
        onLinkedMenu={(p) => setPartnerAction({ partner: p, kind: "unlink" })}
        onShowcaseMenu={(p) => setPartnerAction({ partner: p, kind: "remove" })}
        isCurrentUserAdmin={isCurrentUserAdmin}
      />
    </>
  );

  // V2 page chrome (ARTBOARD-SPEC §2 canvas #F4F6FA + board 1a title
  // row; CMP-TABS chrome; mobile boards 1l/2j). Presentation-only —
  // every handler below is byte-identical to the previous version.
  // Wave-2 follow-up: the board-1a subtitle (F-2) and the trust-stats
  // row (F-1) are now REAL, fed by the BE endpoints from PR #170 via
  // useAgencyShellData (fail-silent — hidden when the fetch errored).
  return (
    <div className="min-h-screen bg-[#F4F6FA] font-tpv2">
      <div className={`mx-auto ${isMobile ? "pb-24" : ""}`}>
        {isMobile ? (
          <>
            <AgencyProfileMobileHeader
              activeTab={activeTab}
              agencyName={shellCounts?.name}
              memberCount={shellCounts?.member_count}
              onInviteAgent={() => setShowAgentInviteModal(true)}
              onInvitePartner={() => setShowPartnerInviteModal(true)}
              onInvitePlayer={() =>
                navigate("/agency/invite-player", { state: { mode: "agency" } })
              }
              onInviteStaff={() =>
                navigate("/agency/invite-staff", { state: { mode: "agency" } })
              }
            />
            <AgencyProfileMobileTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              counts={tabCounts}
            />
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 pt-[26px] px-6">
              {/* Board 1a title row — left column gap 3: title + F-2
                  subtitle "<name> · N members" (tab-counts response;
                  hidden while absent, fail-silent). */}
              <div className="flex flex-col gap-[3px]">
                <h1 className="text-[22px] font-bold text-[#0F172A] tracking-[-.01em]">
                  My Agency
                </h1>
                <AgencySubtitle
                  name={shellCounts?.name}
                  memberCount={shellCounts?.member_count}
                />
              </div>
            </div>
            <AgencyTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              counts={tabCounts}
              onInviteAgent={() => setShowAgentInviteModal(true)}
              onInvitePartner={() => setShowPartnerInviteModal(true)}
              onInvitePlayer={() =>
                navigate("/agency/invite-player", { state: { mode: "agency" } })
              }
              onInviteStaff={() =>
                navigate("/agency/invite-staff", { state: { mode: "agency" } })
              }
            />
          </>
        )}

        {/* Content area — old surface components stay inside the new
            chrome untouched (Phase B replaces them); they carry their
            own px-6 horizontal padding. */}
        <div className="flex-1 pb-[34px]">
          {/* Agents tab: pending invitations (admin) + toolbar + grid */}
          {activeTab === "agents" && (
            <>
              {isCurrentUserAdmin && (
                <PendingInvitationsPanel reloadKey={pendingReloadKey} />
              )}
              <AgentsToolbar
                counts={agentCounts}
                segment={agentSegment}
                onSegment={setAgentSegment}
                search={agentSearch}
                onSearch={setAgentSearch}
                showSegments={isCurrentUserAdmin}
                matchCount={visibleAgents.length}
                isMobile={isMobile}
              />
              {/* F-1 — board 1a trust stats row (real data, BE PR #170),
                  immediately before the card grid per the board order.
                  Zeros still render; an errored fetch hides the row
                  (trustStats stays null). Board 1l has no mobile slot. */}
              {!isMobile && <TrustStatsRow stats={trustStats} />}
              <AgentsGrid
                agents={visibleAgents}
                agentsLoading={agentsLoading}
                agentsError={agentsError}
                showPopover={showPopover}
                popoverAgent={popoverAgent}
                transferringAgentId={transferringAgentId}
                onAgentMenuClick={handleAgentMenuClick}
                onClosePopover={() => setShowPopover(false)}
                onMakeAdmin={handleMakeAdmin}
                onRemoveAdmin={handleRemoveAdmin}
                onRemoveFromAgency={handleRemoveFromAgency}
                onAgentClick={handleAgentClick}
                onAgentUpdated={() => fetchAgents(activeAgentType)}
                isCurrentUserAdmin={isCurrentUserAdmin}
                currentUserAccountId={currentUserAccountId}
                isMobile={isMobile}
                searchQuery={agentSearch}
                segment={agentSegment}
                totalCount={agents.length}
                onInviteAgent={
                  isCurrentUserAdmin ? () => setShowAgentInviteModal(true) : undefined
                }
                onViewAllAgents={() => setAgentSegment("all")}
                adminCount={adminCount}
                // B4: quiet affordance only while the own row is unlicensed.
                onSubmitLicense={
                  ownAgentRow && !isAgentLicensed(ownAgentRow)
                    ? () => setShowSubmitLicense(true)
                    : undefined
                }
                // F-6: existing target — the role-aware FAQ page (agency/
                // agent role sections cover invites, admin roles, license).
                onRolesHelp={() => navigate("/faq")}
              />
            </>
          )}

          {activeTab === "players" && renderPlayersTab()}
          {STAFF_MANDATE_ENABLED && activeTab === "staff" && renderStaffTab()}
          {activeTab === "partners" && renderPartnersTab()}
        </div>

        {/* Agent Invitation Modal */}
        <AgentInvitationModal
          isOpen={showAgentInviteModal}
          onClose={() => setShowAgentInviteModal(false)}
          onSuccess={() => {
            fetchAgents(activeAgentType);
            setPendingReloadKey((k) => k + 1);
            // F-3: same refetch point — counts stay in sync if the
            // invite changed anything countable.
            refreshShellCounts();
          }}
        />

        <PartnerInvitationModal
          isOpen={showPartnerInviteModal}
          onClose={() => setShowPartnerInviteModal(false)}
          onSuccess={refreshPartners}
        />

        {/* B4 "Submit license" — the EXISTING license-submission modal
            (My Profile → Update License), reused 1:1: same fields, same
            updateAgentLicense endpoint; the licensed state flips only
            after backend verification (no new review flow). */}
        {showSubmitLicense && (
          <LicenseUpdateModal
            onClose={() =>
              submittingLicense ? null : setShowSubmitLicense(false)
            }
            onSubmit={handleSubmitLicense}
            isSubmitting={submittingLicense}
            currentLicenseNumber={ownAgentRow?.license_number || ""}
            hasExistingDocument={Boolean(ownAgentRow?.document_url)}
          />
        )}

        {/* Partner destructive confirm (revoke / unlink / remove showcase) —
            V2 CMP-CONFIRM presentation (boards 4c/2i); same action state,
            same confirmPartnerAction handler, close blocked while loading. */}
        <PartnerConfirmDialog
          action={partnerAction}
          loading={partnerActionLoading}
          onCancel={() => (partnerActionLoading ? null : setPartnerAction(null))}
          onConfirm={confirmPartnerAction}
        />

        {/* Change Agent Modal */}
        <ChangeAgentModal
          isOpen={showChangeAgentModal}
          onClose={() => setShowChangeAgentModal(false)}
          player={selectedPlayer}
          onSuccess={handleChangeAgentSuccess}
        />

        {/* TP-Coach-Mandate: Change Agent for mandated staff (staff variant
            modal; the player modal above stays untouched) */}
        {STAFF_MANDATE_ENABLED && (
          <ChangeStaffAgentModal
            isOpen={showChangeStaffAgentModal}
            onClose={() => setShowChangeStaffAgentModal(false)}
            member={selectedStaffMember}
            onSuccess={handleChangeStaffAgentSuccess}
          />
        )}

        {/* Promote / Demote admin confirmation — V2 CMP-CONFIRM 1:1 from
            ARTBOARD-SPEC board 1k (affirmative Make-admin / danger
            Remove-as-admin). Wiring byte-identical: same
            confirmAdminAction/cancelAdminAction handlers, same loading
            guard, and the distinct self-demote warning (reload path,
            FUNCTION-INVENTORY §2.6) survives verbatim. */}
        {(() => {
          const target = adminActionTarget;
          const name = target?.agent?.name || "this user";
          const firstName = String(name).trim().split(/\s+/)[0];
          const promote = target?.action === "promote";
          const isSelf =
            currentUserAccountId != null &&
            String(target?.agent?.account_id) === String(currentUserAccountId);
          return (
            <ConfirmDialog
              open={!!target}
              mobile={isMobile}
              tone={promote ? "affirmative" : "danger"}
              icon={
                promote ? (
                  <ShieldIcon size={18} stroke="#2283FF" />
                ) : (
                  <ShieldXIcon size={18} />
                )
              }
              title={
                promote ? (
                  <>
                    Make <span data-no-translate>{name}</span> an admin?
                  </>
                ) : isSelf ? (
                  <>Remove yourself as admin?</>
                ) : (
                  <>
                    Remove <span data-no-translate>{name}</span> as admin?
                  </>
                )
              }
              body={
                promote ? (
                  <>
                    Admins can invite or remove agents, change roles, and edit
                    agency settings.{" "}
                    <span data-no-translate>{firstName}</span> keeps managing
                    their own players.
                  </>
                ) : isSelf ? (
                  <>
                    You’ll lose access to admin-only screens and the page will
                    refresh. This is blocked if you’re the last admin.
                  </>
                ) : (
                  <>
                    <span data-no-translate>{firstName}</span> stays in the
                    agency as an agent but loses access to member management
                    and agency settings.
                  </>
                )
              }
              confirmLabel={promote ? "Make admin" : "Remove as admin"}
              cancelLabel="Cancel"
              loading={adminActionLoading}
              onClose={cancelAdminAction}
              onConfirm={confirmAdminAction}
            />
          );
        })()}
      </div>
    </div>
  );
};

export default AgencyProfile;
